/**
 * Responsibility: Simple app routing and state management.
 * Scope: Owns state definitions and URL synchronization.
 */
import { router, StateManager } from '@dharmax/state-router'
import { TOPICS } from "@anyquest/shared-core"

export const APP_STATE_DEFS = [
    { name: 'login',       page: 'login-view',            route: /^$/ },
    { name: 'welcome',     page: 'welcome-view',          route: 'welcome' },
    { name: 'hub',         page: 'hub-view',              route: 'hub' },
    { name: 'marketplace', page: 'marketplace-view',      route: 'marketplace' },
    { name: 'saves',       page: 'saves-view',            route: 'saves' },
    { name: 'worlds',      page: 'worlds-view',           route: 'worlds' },
    { name: 'vessels',     page: 'vessels-view',          route: 'vessels' },
    { name: 'pairing',     page: 'pairing-view',          route: 'pairing' },
    { name: 'forging',     page: 'forging-view',          route: 'new' },
    { name: 'admin',       page: 'admin-dashboard-view',   route: 'admin' },
    { name: 'dev',         page: 'dev-observability-view', route: 'dev' },
    { name: 'scene',       page: 'scene-view',            route: 'play/:id' },
    { name: 'error',       page: 'error-view',            route: 'error' },
] as const
export const dispatcher = StateManager.dispatcher
export const stateService = new StateManager('history', false)

export function registerAuthGuard(checkAuth: () => 'loading' | 'signed-in' | 'signed-out' | 'blocked') {
    stateService.onBeforeChange((target) => {
        const status = checkAuth()
        if (target.name === 'login' || status === 'loading' || status === 'signed-in') return true
        if (status === 'blocked') {
            setTimeout(() => stateService.setState('login'), 0)
            return false
        }
        setTimeout(() => stateService.setState('login'), 0)
        return false
    })
}

export function navigateState(name: string, context?: any) {
    const def = APP_STATE_DEFS.find(d => d.name === name)
    if (def) {
        if (typeof def.route === 'string') {
            const url = context ? def.route.replace(':id', context) : def.route
            router.navigate(url)
        } else if (name === 'login') {
            router.navigate('')
        }
    }
    return stateService.setState(name, context)
}

let statesConfigured = false
export function configureAppStates() {
    if (statesConfigured) return
    statesConfigured = true
    for (const def of APP_STATE_DEFS) {
        stateService.addState(def.name, def.page, def.route)
    }
}

export function sysNotify(msg: string): void {
    dispatcher.trigger('app', TOPICS.ui.notify, {
        message: `[${new Date().toLocaleTimeString()}] ${msg}`,
        level: 'info',
    })
}

router.staticFilters.push((url) => url.endsWith('.xml') || url.includes('/api/'))

let devModeEnabled = false
export const setDevMode = (v: boolean): void => { devModeEnabled = v }
export const isDevMode = (): boolean => devModeEnabled
